import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getActivityLog, searchActivityLog } from '../services/api';
import { FaHistory, FaSearch, FaFilter } from 'react-icons/fa';
import '../styles/ActivityLog.css';

function ActivityLog({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, [actionFilter, entityFilter, page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };
      if (actionFilter !== 'all') params.action = actionFilter;
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      const data = await getActivityLog(params);
      const logEntries = data.logs || data.entries || data;
      setLogs(Array.isArray(logEntries) ? logEntries : []);
      setHasMore(Array.isArray(logEntries) && logEntries.length === 50);
    } catch (error) {
      console.error('Error fetching activity log:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchLogs();
      return;
    }
    try {
      setLoading(true);
      const data = await searchActivityLog(searchQuery);
      const logEntries = data.logs || data.entries || data;
      setLogs(Array.isArray(logEntries) ? logEntries : []);
      setHasMore(false);
    } catch (error) {
      console.error('Error searching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      create: '#43b581',
      update: '#5865f2',
      delete: '#f04747',
      sign: '#43b581',
      terminate: '#f04747',
      complete: '#43b581',
      fail: '#f04747',
      login: '#5865f2',
      logout: '#72767d'
    };
    return colors[action] || '#72767d';
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      task: 'T',
      contract: 'C',
      journal: 'J',
      glamour: 'G',
      venue: 'V',
      training: 'Tr',
      scene: 'S',
      rule: 'R',
      punishment: 'P',
      reward: 'Rw',
      user: 'U'
    };
    return icons[entityType] || '?';
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    const date = new Date(ts);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaHistory /> Aktivitätslog</h1>
        </div>

        <div className="search-bar">
          <input
            type="text"
            placeholder="Aktivitäten durchsuchen..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <button className="search-btn" onClick={handleSearch}>
            <FaSearch />
          </button>
        </div>

        <div className="log-filters">
          <div className="filter-group">
            <label><FaFilter /> Aktion:</label>
            <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }}>
              <option value="all">Alle</option>
              <option value="create">Erstellt</option>
              <option value="update">Aktualisiert</option>
              <option value="delete">Gelöscht</option>
              <option value="complete">Abgeschlossen</option>
              <option value="sign">Unterschrieben</option>
              <option value="terminate">Gekündigt</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Entität:</label>
            <select value={entityFilter} onChange={e => { setEntityFilter(e.target.value); setPage(1); }}>
              <option value="all">Alle</option>
              <option value="task">Aufgaben</option>
              <option value="contract">Verträge</option>
              <option value="journal">Journal</option>
              <option value="glamour">Glamours</option>
              <option value="venue">Venues</option>
              <option value="training">Training</option>
              <option value="scene">Szenen</option>
              <option value="rule">Regeln</option>
              <option value="punishment">Bestrafungen</option>
              <option value="reward">Belohnungen</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <div className="log-table-container">
              <table className="log-table">
                <thead>
                  <tr>
                    <th>Zeitpunkt</th>
                    <th>Aktion</th>
                    <th>Entität</th>
                    <th>Beschreibung</th>
                    <th>Benutzer</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length > 0 ? (
                    logs.map((log, index) => (
                      <tr key={log.id || index}>
                        <td className="log-timestamp">{formatTimestamp(log.timestamp || log.created_at)}</td>
                        <td>
                          <span
                            className="action-badge"
                            style={{ backgroundColor: getActionColor(log.action) }}
                          >
                            {log.action}
                          </span>
                        </td>
                        <td>
                          <span className="entity-badge">
                            <span className="entity-icon">{getEntityIcon(log.entity_type)}</span>
                            {log.entity_type}
                          </span>
                        </td>
                        <td className="log-description">{log.description || '-'}</td>
                        <td className="log-user">{log.user_name || log.user_id || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="no-logs">Keine Einträge gefunden</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                className="page-btn"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                Zurück
              </button>
              <span className="page-info">Seite {page}</span>
              <button
                className="page-btn"
                disabled={!hasMore}
                onClick={() => setPage(p => p + 1)}
              >
                Weiter
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default ActivityLog;
